/**
 * The Quick Bytes reader on a phone - the stories pattern, laid out for a
 * thumb.
 *
 *   ▬▬▬▬ ▬▬▬▬ ──── ────          progress: one segment per card
 *   Quadratic formula     🔖 ✕
 *   ALGEBRA FOUNDATIONS · DUE TODAY
 *
 *   ┌──────────────────────┐
 *   │      card design     │      full width, centred in the room left
 *   └──────────────────────┘
 *   algebra · mathematics
 *
 *   [‹]       2 / 5        [›]    in the thumb's reach
 *        UP NEXT · Pythagoras      on a concept's last card
 *
 * The card is centred between the header and the controls. It was centred
 * before too, but under a tall header block with its position as dots at the
 * very bottom, so it read as floating; now the progress bar, header and
 * controls frame it. Nothing said a card could be tapped or swiped, nor
 * that Next runs on into the next concept. Tap halves and swipe still work;
 * the progress bar, the counter between visible ‹ › and the "Up next" line
 * make them findable.
 */

import type { ReactNode, RefObject, TouchEventHandler } from 'react';
import { Bookmark, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import type { HighlightsFeedCarousel } from '@/lib/api-client';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import { stripMath } from '@/lib/markdown/previewText';
import { MAX_HEADER_TAGS } from '@/components/features/mindspace/quick-bytes/CarouselChrome';

const ICON =
  'flex h-11 w-11 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const STEP =
  'flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-elevation-1 transition-transform duration-fast active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:active:scale-100';

interface PhoneCarouselReaderProps {
  carousel: HighlightsFeedCarousel;
  cardIdx: number;
  /** Where Next goes from the last card: the next concept's name, or null. */
  upNext: string | null;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onToggleBookmark: () => void;
  /** Measured by the modal to size the card canvas. */
  cardAreaRef: RefObject<HTMLDivElement>;
  onTouchStart: TouchEventHandler;
  onTouchEnd: TouchEventHandler;
  /** The scaled card, plus the tap zones and any loading overlay. */
  children: ReactNode;
}

export function PhoneCarouselReader({
  carousel,
  cardIdx,
  upNext,
  onPrev,
  onNext,
  onClose,
  onToggleBookmark,
  cardAreaRef,
  onTouchStart,
  onTouchEnd,
  children,
}: PhoneCarouselReaderProps) {
  const total = carousel.cards.length;
  return (
    <div className='flex h-full flex-col pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(0.75rem,env(safe-area-inset-top))]'>
      <Progress total={total} active={cardIdx} />
      <ReaderHeader
        carousel={carousel}
        onClose={onClose}
        onToggleBookmark={onToggleBookmark}
      />
      {/* The card and its tags, centred in the room between the header and
          the controls - the controls stay at the bottom, in the thumb's
          reach. */}
      <div className='flex min-h-0 flex-1 flex-col justify-center py-3'>
        <div
          className='relative mx-4'
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div ref={cardAreaRef} className='aspect-square w-full'>
            {children}
          </div>
        </div>
        {carousel.domain_tags.length > 0 && (
          <p className='mx-4 mt-3 truncate text-sm text-muted-foreground'>
            {carousel.domain_tags.slice(0, MAX_HEADER_TAGS).join(' · ')}
          </p>
        )}
      </div>
      <ReaderSteps
        index={cardIdx}
        total={total}
        upNext={upNext}
        onPrev={onPrev}
        onNext={onNext}
      />
    </div>
  );
}

/** The concept, its lesson and due state, and bookmark + close. */
function ReaderHeader({
  carousel,
  onClose,
  onToggleBookmark,
}: Pick<
  PhoneCarouselReaderProps,
  'carousel' | 'onClose' | 'onToggleBookmark'
>) {
  return (
    <header className='flex items-start gap-1 pl-4 pr-2 pt-2'>
      <div className='min-w-0 flex-1 pt-1.5'>
        <h2 className='text-lg font-semibold leading-snug text-foreground'>
          <InlineMarkdown>{carousel.concept_name}</InlineMarkdown>
        </h2>
        <Meta carousel={carousel} />
      </div>
      <button
        type='button'
        onClick={onToggleBookmark}
        aria-label={carousel.is_bookmarked ? 'Remove bookmark' : 'Add bookmark'}
        aria-pressed={carousel.is_bookmarked}
        className={ICON}
      >
        <Bookmark
          className={cn('h-5 w-5', carousel.is_bookmarked && 'fill-current')}
          aria-hidden='true'
        />
      </button>
      <button
        type='button'
        onClick={onClose}
        aria-label='Close'
        className={ICON}
      >
        <X className='h-5 w-5' aria-hidden='true' />
      </button>
    </header>
  );
}

/** ‹ "2 / 5" › in the thumb's reach, and where Next goes from the last card. */
function ReaderSteps({
  index,
  total,
  upNext,
  onPrev,
  onNext,
}: {
  index: number;
  total: number;
  upNext: string | null;
  onPrev: () => void;
  onNext: () => void;
}) {
  const ahead = index === total - 1 ? upNext : null;
  return (
    <>
      <nav
        aria-label='Cards'
        className='flex items-center justify-between px-4'
      >
        <button
          type='button'
          onClick={onPrev}
          aria-label='Previous card'
          className={STEP}
        >
          <ChevronLeft className='h-5 w-5' aria-hidden='true' />
        </button>
        <p className='font-mono text-sm font-medium tabular-nums text-foreground'>
          {index + 1} / {total}
        </p>
        <button
          type='button'
          onClick={onNext}
          aria-label={ahead ? `Next: ${stripMath(ahead)}` : 'Next card'}
          className={STEP}
        >
          <ChevronRight className='h-5 w-5' aria-hidden='true' />
        </button>
      </nav>
      <p
        aria-live='polite'
        className='flex h-6 items-center justify-center px-4 font-mono text-xs uppercase tracking-[0.1em] text-muted-foreground'
      >
        {ahead && (
          <span className='truncate'>
            Up next ·{' '}
            <InlineMarkdown className='text-foreground'>{ahead}</InlineMarkdown>
          </span>
        )}
      </p>
    </>
  );
}

/** One segment per card; the ones read and the current one are filled. */
function Progress({ total, active }: { total: number; active: number }) {
  return (
    <div aria-hidden='true' className='flex gap-1 px-4'>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          data-active={i === active ? 'true' : undefined}
          className={cn(
            'h-1 flex-1 rounded-full transition-colors duration-300',
            i <= active ? 'bg-foreground' : 'bg-foreground/15'
          )}
        />
      ))}
    </div>
  );
}

/** "ALGEBRA FOUNDATIONS · DUE TODAY", in the label register. */
function Meta({ carousel }: { carousel: HighlightsFeedCarousel }) {
  const parts = [
    carousel.source_lesson_title,
    carousel.is_due_today && 'Due today',
  ]
    .filter(Boolean)
    .join(' · ');
  if (!parts) return null;
  return (
    <p className='mt-1 truncate font-mono text-xs uppercase tracking-[0.08em] text-muted-foreground'>
      {parts}
    </p>
  );
}
